import { useState, useCallback, useEffect } from 'react';

const CLEAR_DATA_DELAY = 300;

interface UsePaginationOptions<T> {
  fetchPage: (page: number) => Promise<{
    results: T[];
    next: number | null;
  }>;
  initialPage?: number;
  /** When false, prevents initial fetch and refetch until enabled. Default: true */
  enabled?: boolean;
}

interface UsePaginationResult<T> {
  data: T[];
  currentPage: number | null;
  hasMore: boolean;
  isLoading: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
  reset: () => void;
  removeItem: (predicate: (item: T) => boolean) => void;
}

export const usePagination = <T, >({
  fetchPage,
  initialPage = 1,
  enabled = true,
}: UsePaginationOptions<T>): UsePaginationResult<T> => {
  const [data, setData] = useState<T[]>([]);
  const [currentPage, setCurrentPage] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const loadMore = useCallback(async() => {
    if (!hasMore || isLoading || currentPage === null) {
      return;
    }

    setIsLoading(true);
    try {
      const nextPage = currentPage + 1;
      const response = await fetchPage(nextPage);
      setData((prev) => [...prev, ...response.results]);
      setCurrentPage(nextPage);
      setHasMore(response.next !== null);
    } catch (err) {
      // Stop trying to load more on error (silently, as 404 is expected when no more pages)
      setHasMore(false);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [fetchPage, hasMore, isLoading, currentPage]);

  const refetch = useCallback(async() => {
    try {
      const response = await fetchPage(initialPage);
      setData(response.results);
      setCurrentPage(initialPage);
      setHasMore(response.next !== null);
    } catch {
      // Silently handle error to prevent crash - data remains unchanged
    }
  }, [fetchPage, initialPage]);

  const reset = useCallback(() => {
    setData([]);
    setCurrentPage(null);
    setHasMore(false);
    setIsLoading(false);
  }, []);

  const removeItem = useCallback((predicate: (item: T) => boolean) => {
    setData((prev) => prev.filter((item) => !predicate(item)));
  }, []);

  // Initial load
  useEffect(() => {
    // Don't fetch if disabled
    if (!enabled) {
      return;
    }

    setIsLoading(true);

    // Clear data after a short delay to avoid flash of empty state
    const clearDataTimeout = setTimeout(() => {
      setData([]);
      setCurrentPage(null);
      setHasMore(false);
    }, CLEAR_DATA_DELAY);

    const fetchInitialData = async() => {
      try {
        const response = await fetchPage(initialPage);
        clearTimeout(clearDataTimeout);
        setData(response.results);
        setCurrentPage(initialPage);
        setHasMore(response.next !== null);
      } catch {
        // Silently handle errors - they will be logged by the network layer
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();

    return () => {
      clearTimeout(clearDataTimeout);
    };
  }, [fetchPage, initialPage, enabled]);

  return {
    data,
    currentPage,
    hasMore,
    isLoading,
    loadMore,
    refetch,
    reset,
    removeItem,
  };
};
